import * as React from "react";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tree } from "antd";
import { observer } from "mobx-react";
import { TableLoader } from "Components/ScrollableTable/components/TableLoader";
import { I18N_NS } from "Config/i18n";
import { withTranslation, WithTranslation } from "react-i18next";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { ExtendedDataNode, objectsAndDataStore } from "../Stores/ObjectsAndDataStore";
import { Link } from "react-router-dom";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";

interface SidebarProps extends WithTranslation, WithModal, WithRouterProps {}

interface SidebarState {
  height: number;
  width: number;
}

export const Sidebar = withTranslation(I18N_NS.PROJECT)(
  withModal(
    withRouter(
      observer(
        class extends React.Component<SidebarProps, SidebarState> {
          constructor(props: SidebarProps) {
            super(props);

            this.state = {
              height: null,
              width: null,
            };
          }

          componentDidMount() {
            this.setSidebarHeight();
            this.makeResizableSidebar();
            window.addEventListener("resize", this.setSidebarHeight);
            this.setSidebarWidth(objectsAndDataStore.sideBarIsOpen);
          }

          componentWillUnmount() {
            window.removeEventListener("resize", this.setSidebarHeight);
          }

          makeResizableSidebar = () => {
            const sidebar: HTMLDivElement = document.querySelector(".documents-sidebar");
            const resizer: HTMLDivElement = document.querySelector(".documents-sidebar-wrapper .resizable");

            const resize = (e) => {
              const size = e.pageX - sidebar.getBoundingClientRect().left;
              sidebar.setAttribute("style", `width: ${size}px; min-width: ${size}px; overflow:hidden;`);
            };

            const stopResize = () => {
              const sidebarWidth = sidebar.style.width.split("px")[0];
              if (Number(sidebarWidth) < 250) {
                this.toggleSidebar();
              }
              window.removeEventListener("mousemove", resize);
              window.removeEventListener("mouseup", stopResize);
            };

            resizer.addEventListener("mousedown", (e: MouseEvent) => {
              e.preventDefault();
              window.addEventListener("mousemove", resize);
              window.addEventListener("mouseup", stopResize);
            });
          };

          setSidebarHeight = () => {
            const documentHeight: number = document.documentElement.clientHeight;
            const headerHeight: number = document.getElementsByTagName("header")[0].clientHeight;
            this.setState({
              height: documentHeight - headerHeight,
            });
          };

          setSidebarWidth = (isOpen: boolean) => {
            const sidebar: HTMLDivElement = document.querySelector(".documents-sidebar");
            let size: number;
            isOpen ? (size = 280) : (size = 15);
            sidebar.setAttribute("style", `width: ${size}px; min-width: ${size}px; overflow: hidden;`);
          };

          toggleSidebar = () => {
            objectsAndDataStore.toggleSidebar();
            this.setSidebarWidth(objectsAndDataStore.sideBarIsOpen);
          };

          handleNodeSelect = (_, info) => {
            const node = info.node as ExtendedDataNode;
            (async () => {
              if (info?.nativeEvent?.target?.className === "objectsanddata_attachments") {
                this.props.navigate(node.link + "/attachments");
              } else {
                this.props.navigate(node.link);
              }
            })();
          };

          handleLoad = async (info) => {
            const node = info as ExtendedDataNode;
            try {
              if (node.children) {
                return await Promise.resolve();
              }
              if (node.dataType === "MAIN") {
                await objectsAndDataStore.fetchMainObjects(node);
              } else {
                await objectsAndDataStore.fetchSubObjects(node);
              }
            } catch (error) {
              throw error;
            }
          };

          renderTitle = (node: ExtendedDataNode) => {
            return (
              <>
                <Link to={node.link}>
                  <>{node.title}</>
                </Link>
                {node.attachmentCount > 0 && (
                  <span className="objectsanddata_attachments">
                    {" (" + node.attachmentCount + " " + this.props.t("Attachments") + ")"}
                  </span>
                )}
              </>
            );
          };

          render() {
            const { sideBarIsOpen } = objectsAndDataStore;
            const sidebarClassName = classnames("documents-sidebar", { "is-open": sideBarIsOpen });
            const toggleIconClassName = classnames("toggle-icon", { "is-open": sideBarIsOpen });
            const toggleResizerClassName = classnames("resizable", { block: sideBarIsOpen });
            const isNotEmpty = !!objectsAndDataStore.data?.length;

            return (
              <div
                className="documents-sidebar-wrapper objects-and-data-sidebar-wrapper"
                style={{ height: this.state.height }}
              >
                <div className={sidebarClassName} style={{ overflow: "hidden" }}>
                  <TableLoader
                    loading={objectsAndDataStore.state === "LOADING" && !objectsAndDataStore.treeData}
                    style={{ backgroundColor: "#e0e5ed" }}
                  />
                  {isNotEmpty && (
                    <Tree
                      height={this.state.height}
                      blockNode={true}
                      onSelect={this.handleNodeSelect}
                      loadData={this.handleLoad}
                      style={{ background: "transparent", fontSize: 13 }}
                      treeData={[...objectsAndDataStore.treeData]}
                      titleRender={this.renderTitle}
                    />
                  )}
                </div>
                <div className={toggleResizerClassName}>
                  <div className="line" />
                </div>
                <div className={toggleIconClassName} onClick={this.toggleSidebar}>
                  <div className="toggle-background" />
                  <FontAwesomeIcon icon={["far", "chevron-right"]} size={"lg"} className="documents-sidebar-icon" />
                </div>
              </div>
            );
          }
        }
      )
    )
  )
);
